/**
 * Repère tarifaire réglementé — miroir de `backend/regulated_reference.py`.
 *
 * La valeur 0,2516 €/kWh (et 13,83 €/mois) traînait à cinq endroits du frontend
 * comme « le tarif réglementé ». C'était la grille d'environ 2024. Le PriceTicker
 * l'affichait publiquement, et le comparateur pré-remplissait le formulaire avec.
 *
 * Conséquence : facture de référence surévaluée d'environ 23 %, donc des économies
 * affichées qui n'existaient pas.
 *
 * Source : grille de prix EDF « Tarif Bleu », applicable au 1ᵉʳ février 2026.
 * https://particulier.edf.fr/content/dam/2-Actifs/Documents/Offres/Grille_prix_Tarif_Bleu.pdf
 *
 * ⚠️  À mettre à jour à chaque révision du TRV (typiquement 1ᵉʳ février / 1ᵉʳ août).
 *     La source de vérité côté serveur est la collection `regulated_tariffs`,
 *     exposée par GET /api/regulated-tariffs/fr : préférer cet endpoint quand un
 *     composant peut faire un appel réseau.
 */

export const TRV_VALID_FROM = "2026-02-01";

/** Option Base, 6 kVA — cas de référence des particuliers. */
export const TRV_BASE_PRICE_EUR_KWH = 0.194;
export const TRV_BASE_SUBSCRIPTION_EUR_MONTH = 15.65;

/** Option Heures Creuses (identique de 6 à 36 kVA). */
export const TRV_HC_PRICE_HP_EUR_KWH = 0.2065;
export const TRV_HC_PRICE_HC_EUR_KWH = 0.1579;

/** Prix du kWh en option Base selon le palier de puissance. */
export const TRV_BASE_PRICE_BY_KVA = {
    3: 0.194, 6: 0.194,
    9: 0.1927, 12: 0.1927, 15: 0.1927, 18: 0.1927,
    24: 0.1927, 30: 0.1927, 36: 0.1927,
};

/** Abonnement mensuel TTC par puissance. */
export const TRV_SUBSCRIPTION_BY_KVA = {
    3: 12.03, 6: 15.65, 9: 19.56, 12: 23.32, 15: 26.84,
    18: 30.49, 24: 38.24, 30: 45.37, 36: 52.54,
};

/** Facture annuelle TTC au tarif réglementé (option Base). */
export function regulatedAnnualCostEur(annualKwh, puissanceKva = 6) {
    const price = TRV_BASE_PRICE_BY_KVA[puissanceKva] ?? TRV_BASE_PRICE_EUR_KWH;
    const sub = TRV_SUBSCRIPTION_BY_KVA[puissanceKva] ?? TRV_BASE_SUBSCRIPTION_EUR_MONTH;
    return Math.round((annualKwh * price + sub * 12) * 100) / 100;
}

/** Mention de périmètre — obligatoire dès qu'un classement d'offres est affiché. */
export const SCOPE_NOTICE =
    "Classement établi sur nos offres partenaires uniquement. Il ne s'agit pas " +
    "d'un comparatif exhaustif du marché. Le tarif réglementé est indiqué comme " +
    "repère indépendant.";
